import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { locationsRes, safe } from "../../lib/locations";
import { toast } from "sonner";
import { Plus, Trash, Clock, Calendar, X } from "@phosphor-icons/react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const EMPTY_HOLIDAY = {
  id: null, name: "", notes: "",
  dates: [],
  all_scope: true, district_ids: [], lsgd_ids: [], zone_ids: [],
  recurring: false,
};

export default function Schedules() {
  const [tab, setTab] = useState("schedules");
  const [items, setItems] = useState([]);
  const [zones, setZones] = useState([]);
  const [locations, setLocations] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [hForm, setHForm] = useState(EMPTY_HOLIDAY);
  const [hDrawer, setHDrawer] = useState(false);
  const [hDateInput, setHDateInput] = useState("");
  const [hBusy, setHBusy] = useState(false);
  const [form, setForm] = useState({
    district_id: "", lsgd_id: "", target: "zone",
    zone_id: "", ward_id: "",
    days: new Set(),
    start_time: "06:00", end_time: "10:00",
  });

  const load = async () => {
    try {
      const [s, z, l, h] = await Promise.all([
        safe(api.get("/schedules")), safe(api.get("/zones")),
        locationsRes(), safe(api.get("/holidays")),
      ]);
      setItems(s.data); setZones(z.data); setLocations(l.data);
      setHolidays(h.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const districts = useMemo(() => locations.filter((x) => x.type === "district"), [locations]);
  const lsgds = useMemo(
    () => locations.filter((x) => x.type === "lsgd" && (!form.district_id || x.parent_id === form.district_id)),
    [locations, form.district_id],
  );
  const lsgdWards = useMemo(
    () => locations.filter((w) => w.type === "ward" && (!form.lsgd_id || w.parent_id === form.lsgd_id)),
    [locations, form.lsgd_id],
  );
  const lsgdZones = useMemo(
    () => zones.filter((z) => !form.lsgd_id || z.lsgd_id === form.lsgd_id),
    [zones, form.lsgd_id],
  );

  const wards = useMemo(() => locations.filter((x) => x.type === "ward"), [locations]);

  const toggleDay = (d) => {
    const s = new Set(form.days);
    if (s.has(d)) s.delete(d); else s.add(d);
    setForm(f => ({ ...f, days: s }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.lsgd_id) { toast.error("Select LSGD"); return; }
    if (form.days.size === 0) { toast.error("Select at least one day"); return; }
    try {
      const payload = {
        zone_id: form.target === "zone" ? form.zone_id || null : null,
        ward_id: form.target === "ward" ? form.ward_id || null : null,
        days: Array.from(form.days),
        start_time: form.start_time,
        end_time: form.end_time,
      };
      if (!payload.zone_id && !payload.ward_id) {
        toast.error(`Pick a ${form.target}`); return;
      }
      await api.post("/schedules", payload);
      toast.success("Schedule added");
      setForm((f) => ({ ...f, days: new Set(), zone_id: "", ward_id: "" }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete schedule?")) return;
    try { await api.delete(`/schedules/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const zoneName = (id) => zones.find(z => z.id === id)?.name || "—";
  const wardName = (id) => wards.find(w => w.id === id)?.name || "—";
  const districtName = (id) => locations.find(l => l.id === id)?.name || id;
  const lsgdName = (id) => locations.find(l => l.id === id)?.name || id;

  // ---------- Holiday CRUD handlers ----------
  const openNewHoliday = () => { setHForm(EMPTY_HOLIDAY); setHDateInput(""); setHDrawer(true); };
  const openEditHoliday = (h) => {
    setHForm({
      id: h.id, name: h.name || "", notes: h.notes || "",
      dates: h.dates || [],
      all_scope: !!h.all_scope,
      district_ids: h.district_ids || [], lsgd_ids: h.lsgd_ids || [], zone_ids: h.zone_ids || [],
      recurring: !!h.recurring,
    });
    setHDateInput("");
    setHDrawer(true);
  };
  const closeHoliday = () => setHDrawer(false);
  const toggleHolidayId = (field, id) => {
    setHForm(f => {
      const cur = new Set(f[field]);
      if (cur.has(id)) cur.delete(id); else cur.add(id);
      return { ...f, [field]: Array.from(cur) };
    });
  };
  const addHDate = () => {
    const d = (hDateInput || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) { toast.error("Pick a valid date"); return; }
    if (hForm.dates.includes(d)) { toast("Already added"); return; }
    setHForm(f => ({ ...f, dates: [...f.dates, d].sort() }));
    setHDateInput("");
  };
  const removeHDate = (d) => setHForm(f => ({ ...f, dates: f.dates.filter(x => x !== d) }));
  const submitHoliday = async () => {
    if (!hForm.name.trim()) { toast.error("Holiday name is required"); return; }
    if (hForm.dates.length === 0) { toast.error("Add at least one date"); return; }
    if (!hForm.all_scope && hForm.district_ids.length === 0
        && hForm.lsgd_ids.length === 0 && hForm.zone_ids.length === 0) {
      toast.error("Pick a scope (all tenant or districts/LSGDs/zones)"); return;
    }
    setHBusy(true);
    try {
      const payload = { ...hForm }; delete payload.id;
      if (hForm.id) {
        await api.put(`/holidays/${hForm.id}`, payload); toast.success("Updated");
      } else {
        await api.post("/holidays", payload); toast.success("Created");
      }
      closeHoliday(); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setHBusy(false);
  };
  const deleteHoliday = async (h) => {
    if (!window.confirm(`Delete holiday "${h.name}"?`)) return;
    try { await api.delete(`/holidays/${h.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const hLsgds = useMemo(() => {
    if (hForm.district_ids.length === 0) return locations.filter(x => x.type === "lsgd");
    return locations.filter(x => x.type === "lsgd" && hForm.district_ids.includes(x.parent_id));
  }, [locations, hForm.district_ids]);
  const hZones = useMemo(() => {
    if (hForm.lsgd_ids.length === 0) {
      if (hForm.district_ids.length === 0) return zones;
      return zones.filter(z => hForm.district_ids.includes(z.district_id));
    }
    return zones.filter(z => hForm.lsgd_ids.includes(z.lsgd_id));
  }, [zones, hForm.district_ids, hForm.lsgd_ids]);

  return (
    <AppShell title="Collection Schedule" subtitle="Master Control">
      {/* Tabs — schedules ↔ holidays */}
      <div className="flex gap-1 mb-6 border-b border-[#E5E2D9]" data-testid="sched-tabs">
        {[
          { key: "schedules", label: "Pickup Schedules" },
          { key: "holidays",  label: `Holidays (${holidays.length})` },
        ].map(t => (
          <button key={t.key} data-testid={`tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`px-4 h-10 text-sm border-b-2 -mb-px ${tab === t.key ? "border-[#2D5A27] text-[#2D5A27] font-semibold" : "border-transparent text-[#5C5A55] hover:text-[#1A1A1A]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "schedules" && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#E5E2D9] rounded-md">
          <div className="px-6 py-4 border-b border-[#E5E2D9]">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">All schedules</div>
            <div className="font-heading text-lg">Pickup days and time slots</div>
          </div>
          <table className="w-full text-sm" data-testid="schedules-table">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <th className="px-6 py-3">Target</th>
                <th className="px-6 py-3">Days</th>
                <th className="px-6 py-3">Time</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-12 text-center text-[#8A8780]">No schedules yet.</td></tr>
              )}
              {items.map((s, i) => (
                <tr key={s.id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                  <td className="px-6 py-3">
                    {s.zone_id ? <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#2D5A27]" /> Zone: <b>{zoneName(s.zone_id)}</b></span>
                              : <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[#C25D38]" /> Ward: <b>{wardName(s.ward_id)}</b></span>}
                  </td>
                  <td className="px-6 py-3">
                    <div className="flex flex-wrap gap-1">
                      {DAYS.map(d => (
                        <span key={d} className={`text-[10px] uppercase tracking-[0.15em] px-1.5 py-0.5 border rounded ${s.days.includes(d) ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "border-[#E5E2D9] text-[#8A8780]"}`}>
                          {d}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-3 font-mono text-[13px]">{s.start_time} – {s.end_time}</td>
                  <td className="px-6 py-3 text-right">
                    <button data-testid={`sched-del-${s.id}`} onClick={() => del(s.id)}
                            className="text-[#C25D38] inline-flex items-center gap-1 text-xs"><Trash size={12} />Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-[#E5E2D9] rounded-md p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">New schedule</div>
          <div className="font-heading text-lg mb-4">Assign pickup slot</div>
          <form onSubmit={submit} className="space-y-4" data-testid="schedule-form">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">District *</label>
              <select data-testid="sched-district" value={form.district_id}
                      onChange={(e) => setForm(f => ({ ...f, district_id: e.target.value, lsgd_id: "", zone_id: "", ward_id: "" }))}
                      className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white">
                <option value="">— select district —</option>
                {districts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">LSGD *</label>
              <select data-testid="sched-lsgd" value={form.lsgd_id}
                      onChange={(e) => setForm(f => ({ ...f, lsgd_id: e.target.value, zone_id: "", ward_id: "" }))}
                      disabled={!form.district_id}
                      className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white disabled:bg-[#F7F6F2]">
                <option value="">{form.district_id ? "— select LSGD —" : "— select district first —"}</option>
                {lsgds.map((l) => <option key={l.id} value={l.id}>{l.name}{l.lsgd_type ? ` (${l.lsgd_type.replace("_", " ")})` : ""}</option>)}
              </select>
            </div>

            <div className="flex gap-2">
              <button type="button" onClick={() => setForm(f => ({ ...f, target: "zone", ward_id: "" }))}
                      data-testid="tgt-zone"
                      className={`flex-1 h-9 rounded-md text-sm border ${form.target === "zone" ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "border-[#E5E2D9]"}`}>Health Zone</button>
              <button type="button" onClick={() => setForm(f => ({ ...f, target: "ward", zone_id: "" }))}
                      data-testid="tgt-ward"
                      className={`flex-1 h-9 rounded-md text-sm border ${form.target === "ward" ? "bg-[#D4E0D1] border-[#2D5A27] text-[#1A3317]" : "border-[#E5E2D9]"}`}>Ward</button>
            </div>

            {form.target === "zone" ? (
              <select data-testid="sched-zone" value={form.zone_id}
                      onChange={(e) => setForm(f => ({ ...f, zone_id: e.target.value }))}
                      disabled={!form.lsgd_id}
                      className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white disabled:bg-[#F7F6F2]">
                <option value="">{form.lsgd_id ? "— select health zone —" : "— select LSGD first —"}</option>
                {lsgdZones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
              </select>
            ) : (
              <select data-testid="sched-ward" value={form.ward_id}
                      onChange={(e) => setForm(f => ({ ...f, ward_id: e.target.value }))}
                      disabled={!form.lsgd_id}
                      className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white disabled:bg-[#F7F6F2]">
                <option value="">{form.lsgd_id ? "— select ward —" : "— select LSGD first —"}</option>
                {lsgdWards.map(w => <option key={w.id} value={w.id}>{w.ward_no ? `#${w.ward_no} ` : ""}{w.name}</option>)}
              </select>
            )}
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-2">Days</div>
              <div className="grid grid-cols-7 gap-1">
                {DAYS.map(d => (
                  <button type="button" key={d}
                          data-testid={`day-${d}`}
                          onClick={() => toggleDay(d)}
                          className={`h-10 text-xs font-medium rounded border transition-colors ${form.days.has(d) ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white border-[#E5E2D9] text-[#5C5A55] hover:bg-[#F7F6F2]"}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">Start</label>
                <input data-testid="sched-start" type="time" value={form.start_time}
                       onChange={(e) => setForm(f => ({ ...f, start_time: e.target.value }))}
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-2">End</label>
                <input data-testid="sched-end" type="time" value={form.end_time}
                       onChange={(e) => setForm(f => ({ ...f, end_time: e.target.value }))}
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md" />
              </div>
            </div>
            <button data-testid="sched-save" type="submit"
                    className="w-full h-10 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
              <Plus size={14} weight="bold"/>Add schedule
            </button>
          </form>
        </div>
      </div>
      )}

      {tab === "holidays" && (
        <div className="space-y-4" data-testid="holidays-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-[#8A8780]">Master Control</div>
              <div className="font-heading text-lg">Holidays &amp; no-service days</div>
              <div className="text-xs text-[#5C5A55] mt-1 max-w-2xl">
                Dates on which no service request can be raised. Optionally scope a
                holiday to specific districts / LSGDs / health zones. Mark as
                &quot;yearly recurring&quot; to auto-apply every subsequent year.
              </div>
            </div>
            <button data-testid="new-holiday-btn" onClick={openNewHoliday}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#2D5A27] text-white text-sm font-medium hover:bg-[#3A7033]">
              <Plus weight="bold" /> New Holiday
            </button>
          </div>

          <div className="grid gap-3">
            {holidays.length === 0 && (
              <div className="border border-dashed border-[#E5E2D9] rounded-lg p-10 text-center text-[#8A8780]">
                No holidays yet. Click <b>New Holiday</b> to block dates.
              </div>
            )}
            {holidays.map(h => (
              <div key={h.id} data-testid={`holiday-row-${h.id}`}
                   className="border border-[#E5E2D9] rounded-lg bg-white p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Calendar size={16} className="text-[#C25D38]" />
                      <span className="font-semibold text-[#1A1A1A]">{h.name}</span>
                      {h.recurring && (
                        <span className="text-[10px] uppercase tracking-[0.15em] bg-[#F0EDF7] border border-[#3B2C7F]/25 text-[#3B2C7F] px-1.5 py-0.5 rounded">
                          Yearly recurring
                        </span>
                      )}
                    </div>
                    {h.notes && <div className="text-sm text-[#5C5A55] mt-1 whitespace-pre-wrap">{h.notes}</div>}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {h.all_scope
                        ? <span className="text-[10px] uppercase tracking-[0.15em] bg-[#F9E4B7] text-[#7A5A11] px-1.5 py-0.5 rounded border border-[#E5DAB7]">All zones</span>
                        : (
                          <>
                            {(h.district_ids || []).map(id => (
                              <span key={id} className="text-[10px] uppercase tracking-[0.1em] bg-[#F7F6F2] border border-[#E5E2D9] text-[#5C5A55] px-1.5 py-0.5 rounded">
                                {districtName(id)}
                              </span>
                            ))}
                            {(h.lsgd_ids || []).map(id => (
                              <span key={id} className="text-[10px] uppercase tracking-[0.1em] bg-[#F7F6F2] border border-[#E5E2D9] text-[#5C5A55] px-1.5 py-0.5 rounded">
                                {lsgdName(id)}
                              </span>
                            ))}
                            {(h.zone_ids || []).map(id => (
                              <span key={id} className="text-[10px] uppercase tracking-[0.1em] bg-[#EDF7EA] border border-[#2D5A27]/25 text-[#1A3317] px-1.5 py-0.5 rounded">
                                {zoneName(id)}
                              </span>
                            ))}
                          </>
                        )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(h.dates || []).slice(0, 10).map(d => (
                        <span key={d} className="text-[11px] font-mono bg-[#FCE9E9] border border-[#C25D38]/30 text-[#7A2E18] px-2 py-0.5 rounded">
                          {d}
                        </span>
                      ))}
                      {(h.dates || []).length > 10 && (
                        <span className="text-[11px] text-[#8A8780] self-center">+{h.dates.length - 10} more</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button data-testid={`h-edit-${h.id}`} onClick={() => openEditHoliday(h)}
                            className="h-9 px-3 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
                      Edit
                    </button>
                    <button data-testid={`h-del-${h.id}`} onClick={() => deleteHoliday(h)}
                            className="h-9 w-9 border border-[#E5E2D9] rounded-md text-[#8A2C2C] hover:bg-[#FCE9E9] inline-flex items-center justify-center">
                      <Trash size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {hDrawer && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center overflow-y-auto p-4">
          <div className="bg-[#FAFAF7] rounded-lg shadow-xl w-full max-w-2xl mt-8 mb-8">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E2D9]">
              <h3 className="font-heading text-lg text-[#1A1A1A]">
                {hForm.id ? "Edit Holiday" : "New Holiday"}
              </h3>
              <button onClick={closeHoliday} className="text-[#8A8780] hover:text-[#1A1A1A]"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.15em] text-[#5C5A55] block mb-1.5">Name *</label>
                <input data-testid="h-name" value={hForm.name}
                       onChange={e => setHForm(f => ({...f, name: e.target.value}))}
                       maxLength={140}
                       placeholder="Republic Day, Onam, Local Festival…"
                       className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md bg-white" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.15em] text-[#5C5A55] block mb-1.5">Notes (optional)</label>
                <textarea data-testid="h-notes" value={hForm.notes}
                          onChange={e => setHForm(f => ({...f, notes: e.target.value}))}
                          rows={2} maxLength={500}
                          className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md bg-white" />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input data-testid="h-recurring" type="checkbox" checked={hForm.recurring}
                       onChange={e => setHForm(f => ({...f, recurring: e.target.checked}))} />
                Yearly recurring (match same month-day every year)
              </label>

              <div>
                <div className="text-[11px] uppercase tracking-[0.15em] text-[#5C5A55] mb-1.5">Dates</div>
                <div className="flex gap-2 items-center">
                  <input data-testid="h-date-input" type="date" value={hDateInput}
                         onChange={e => setHDateInput(e.target.value)}
                         className="h-10 px-3 border border-[#E5E2D9] rounded-md bg-white" />
                  <button type="button" data-testid="h-date-add" onClick={addHDate}
                          className="h-10 px-4 border border-[#2D5A27] text-[#2D5A27] rounded-md text-sm hover:bg-[#EDF7EA]">
                    <Plus size={14} weight="bold" className="inline mr-1" />Add
                  </button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {hForm.dates.map(d => (
                    <span key={d} className="inline-flex items-center gap-1 bg-[#FCE9E9] border border-[#C25D38]/30 text-[#7A2E18] px-2 py-1 rounded text-[11px] font-mono">
                      {d}
                      <button type="button" onClick={() => removeHDate(d)} className="text-[#8A2C2C]"><X size={11} weight="bold" /></button>
                    </span>
                  ))}
                  {hForm.dates.length === 0 && <span className="text-xs text-[#8A8780]">No dates yet</span>}
                </div>
              </div>

              <div className="pt-3 border-t border-[#E5E2D9]">
                <label className="flex items-center gap-2 text-sm mb-3">
                  <input data-testid="h-all-scope" type="checkbox" checked={hForm.all_scope}
                         onChange={e => setHForm(f => ({...f, all_scope: e.target.checked,
                                                          district_ids: e.target.checked ? [] : f.district_ids,
                                                          lsgd_ids:     e.target.checked ? [] : f.lsgd_ids,
                                                          zone_ids:     e.target.checked ? [] : f.zone_ids}))} />
                  Apply to all districts, LSGDs and zones (tenant-wide)
                </label>

                {!hForm.all_scope && (
                  <div className="space-y-3">
                    <PickerRow label="Districts"
                                items={locations.filter(l => l.type === "district")}
                                selectedIds={hForm.district_ids}
                                onToggle={(id) => toggleHolidayId("district_ids", id)}
                                testid="h-districts" />
                    <PickerRow label="LSGDs"
                                items={hLsgds}
                                selectedIds={hForm.lsgd_ids}
                                onToggle={(id) => toggleHolidayId("lsgd_ids", id)}
                                testid="h-lsgds" />
                    <PickerRow label="Health Zones"
                                items={hZones}
                                selectedIds={hForm.zone_ids}
                                onToggle={(id) => toggleHolidayId("zone_ids", id)}
                                testid="h-zones" />
                  </div>
                )}
              </div>
            </div>
            <div className="px-5 py-4 flex gap-2 justify-end border-t border-[#E5E2D9]">
              <button onClick={closeHoliday} className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">Cancel</button>
              <button data-testid="h-save" disabled={hBusy} onClick={submitHoliday}
                      className="h-10 px-5 bg-[#2D5A27] text-white rounded-md text-sm font-medium hover:bg-[#3A7033] disabled:opacity-50">
                {hBusy ? "Saving…" : (hForm.id ? "Save changes" : "Create holiday")}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}

const PickerRow = ({ label, items, selectedIds, onToggle, testid }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.15em] text-[#5C5A55] mb-1.5">{label} ({items.length})</div>
    <div data-testid={testid} className="max-h-40 overflow-y-auto border border-[#E5E2D9] rounded-md p-2 bg-[#FAFAF7]">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
        {items.length === 0 && <div className="text-[11px] text-[#8A8780] col-span-full py-2 px-1">None available</div>}
        {items.map(x => (
          <label key={x.id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-[#EDF7EA] cursor-pointer">
            <input type="checkbox" checked={selectedIds.includes(x.id)} onChange={() => onToggle(x.id)} />
            <span className="text-xs text-[#1A1A1A] truncate">{x.name}</span>
          </label>
        ))}
      </div>
    </div>
  </div>
);
